import { ChevronRight, Ellipsis, Search } from "lucide-react";
import { useEffect, useRef } from "react";
import type { TermSort, TermSummary } from "@termclear/contracts";
import { BrandMark } from "./BrandMark";
import { TermTitle } from "./TermTitle";

export interface LibraryFilters {
  search: string;
  collection: "technical" | "english" | "all";
  category: string;
  state: "all" | "stars-high" | "conversation" | "marker";
  sort: TermSort;
}

export function LibraryView(props: {
  items: TermSummary[];
  total: number;
  categories: Array<{ value: string; count: number }>;
  collections: Array<{ value: "technical" | "english"; count: number }>;
  loading: boolean;
  hasMore: boolean;
  filters: LibraryFilters;
  onFilters: (next: LibraryFilters) => void;
  onLoadMore: () => void;
  onOpen: (termId: string, review: boolean) => void;
  onStartReview: () => void;
  menuOpen: boolean;
  onToggleMenu: () => void;
  onLogout: () => void;
}) {
  const { filters } = props;
  const loadMoreMarker = useRef<HTMLDivElement>(null);
  const loadMore = useRef(props.onLoadMore);
  useEffect(() => {
    loadMore.current = props.onLoadMore;
  }, [props.onLoadMore]);
  useEffect(() => {
    const marker = loadMoreMarker.current;
    if (!marker || !props.hasMore || props.loading || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadMore.current();
    }, { rootMargin: "600px 0px" });
    observer.observe(marker);
    return () => observer.disconnect();
  }, [props.hasMore, props.loading]);
  const hasNoTermsOrFilters = props.total === 0
    && filters.search === ""
    && filters.collection === "all"
    && filters.category === "all"
    && filters.state === "all";
  const update = <K extends keyof LibraryFilters>(key: K, value: LibraryFilters[K]) => {
    props.onFilters({ ...filters, [key]: value, ...(key === "collection" ? { category: "all" } : {}) });
  };
  const collectionCounts = new Map(props.collections.map((collection) => [collection.value, collection.count]));
  const allCollectionCount = props.collections.reduce((total, collection) => total + collection.count, 0);
  return (
    <section className="library-view view" aria-labelledby="library-title">
      <div className="library-heading">
        <div className="library-brand">
          <BrandMark className="library-brand-mark" />
          <div className="library-brand-copy">
            <h1 id="library-title">词库</h1>
            <p>找回想真正弄懂的概念与英语。</p>
          </div>
        </div>
        <div className="library-heading-actions">
          <button
            className="primary-button start-review"
            type="button"
            onClick={props.onStartReview}
            disabled={props.items.length === 0}
          >
            浏览与复习
          </button>
          <button
            className="icon-button"
            type="button"
            aria-label="打开词库菜单"
            aria-expanded={props.menuOpen}
            onClick={props.onToggleMenu}
          >
            <Ellipsis aria-hidden="true" />
          </button>
          {props.menuOpen ? (
            <div className="small-menu library-menu">
              <button type="button" onClick={props.onLogout}>退出登录</button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="library-tools" aria-label="词库工具">
        <label className="search-field">
          <span className="visually-hidden">搜索词库</span>
          <Search aria-hidden="true" />
          <input
            type="search"
            placeholder="搜索词条或解释"
            value={filters.search}
            onChange={(event) => update("search", event.target.value)}
          />
        </label>
        <Tool label="词条类型" className="collection-control">
          <select
            value={filters.collection}
            onChange={(event) => update("collection", event.target.value as LibraryFilters["collection"])}
          >
            <option value="technical">专业术语（{collectionCounts.get("technical") ?? 0}）</option>
            <option value="english">英语词汇（{collectionCounts.get("english") ?? 0}）</option>
            <option value="all">全部词条（{allCollectionCount}）</option>
          </select>
        </Tool>
        <Tool label="分类">
          <select value={filters.category} onChange={(event) => update("category", event.target.value)}>
            <option value="all">全部分类</option>
            {props.categories.map((category) => (
              <option value={category.value} key={category.value}>
                {category.value}（{category.count}）
              </option>
            ))}
          </select>
        </Tool>
        <Tool label="状态">
          <select
            value={filters.state}
            onChange={(event) => update("state", event.target.value as LibraryFilters["state"])}
          >
            <option value="all">全部状态</option>
            <option value="stars-high">需理解 4–5 星</option>
            <option value="conversation">有对话</option>
            <option value="marker">有标记</option>
          </select>
        </Tool>
        <Tool label="排序">
          <select value={filters.sort} onChange={(event) => update("sort", event.target.value as TermSort)}>
            <option value="random">随机（默认）</option>
            <option value="name-asc">名称 A–Z</option>
            <option value="name-desc">名称 Z–A</option>
            <option value="stars-desc">需理解：高到低</option>
            <option value="stars-asc">需理解：低到高</option>
            <option value="question-desc">最近疑问：新到旧</option>
            <option value="question-asc">最近疑问：旧到新</option>
            <option value="recent">最近查看</option>
          </select>
        </Tool>
      </div>

      <div className="library-status" aria-live="polite">
        <span>{props.total} 个词条</span>
        <span>{filters.sort === "random" ? "本轮随机顺序固定，加载更多时不会跳位。" : "排序只改变当前词库视图。"}</span>
      </div>

      <div className="term-list" aria-busy={props.loading}>
        <div className="term-list-header" aria-hidden="true">
          <span>词条</span>
          <span>解释（预览）</span>
          <span>分类</span>
          <span>需理解</span>
          <span>对话与标记</span>
          <span>更新于</span>
        </div>
        {props.items.map((term) => (
          <button className="term-row" type="button" key={term.id} onClick={() => props.onOpen(term.id, false)}>
            <span className="term-name"><TermTitle name={term.name} format={term.titleFormat} /></span>
            <span className="term-summary">{term.summary}</span>
            <span className="term-category">{term.category}</span>
            <span className="term-stars" aria-label={`需理解 ${term.needUnderstanding} 星`}>
              <small>需理解</small>
              <strong aria-hidden="true">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <span className={rating <= term.needUnderstanding ? "is-filled" : "is-empty"} key={rating}>
                    {rating <= term.needUnderstanding ? "★" : "☆"}
                  </span>
                ))}
              </strong>
            </span>
            <span className="term-activity">
              {term.conversationCount ? `${term.conversationCount} 次对话` : "无对话"}
              {term.markerCount ? ` · ${term.markerCount} 标记` : ""}
            </span>
            <time className="term-time" dateTime={term.updatedAt} title={formatFullTime(term.updatedAt)}>
              更新于 {formatTime(term.updatedAt)}
            </time>
            <ChevronRight className="term-row-chevron" aria-hidden="true" />
          </button>
        ))}
      </div>

      {!props.loading && props.items.length === 0 ? (
        <div className="empty-state">
          <h2>{hasNoTermsOrFilters ? "词库还是空的" : "没有符合条件的词条"}</h2>
          <p>{hasNoTermsOrFilters
            ? "从本地 Kit 导入第一批词条后，就可以开始浏览与复习。"
            : "试试清除搜索，或者换一个分类与状态。"}</p>
        </div>
      ) : null}
      <div className="load-more" ref={loadMoreMarker} aria-live="polite">
        {props.loading && props.items.length > 0 ? <span>正在自动加载更多…</span> : null}
        {props.hasMore && typeof IntersectionObserver === "undefined" ? (
          <button className="text-button" type="button" onClick={props.onLoadMore}>加载下一页</button>
        ) : null}
        {!props.hasMore && props.items.length > 0 ? <span>已显示全部 {props.total} 个词条</span> : null}
      </div>
    </section>
  );
}

function Tool({ label, children, className = "" }: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`tool-control ${className}`.trim()}>
      <span className="tool-label">{label}</span>
      {children}
    </label>
  );
}

function formatTime(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", { month: "numeric", day: "numeric" }).format(new Date(value));
}

function formatFullTime(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}
